import { createFileRoute } from "@tanstack/react-router";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/Sections";

const URL = "https://finendra.com/terms";
const OG = "https://finendra.com/og/og-default.png";

const sections = [
  {
    h: "About these terms",
    p: [
      "These terms govern your use of the Finendra website. They are not the agreement for accounting services — engagements are governed by a separate written service agreement signed by both parties.",
      "If anything on this page conflicts with a signed engagement agreement, the engagement agreement controls.",
    ],
  },
  {
    h: "Use of this site",
    p: [
      "You may use this site to learn about our services, request proposals, and build indicative quotes. You agree not to misuse the site, attempt to access areas you are not authorised to reach, or interfere with its normal operation.",
      "Administrative areas of this site are restricted to authorised Finendra personnel.",
    ],
  },
  {
    h: "Quotes and estimates",
    p: [
      "Rates, calculator outputs, and downloadable quotes are indicative and based on the inputs you provide. They are not a binding offer.",
      "Final pricing is confirmed in writing after a discovery call, once scope, transaction volume, and service levels are agreed.",
      "Quotes are valid for the period stated on the document unless we confirm otherwise in writing.",
    ],
  },
  {
    h: "Engagement basics",
    p: [
      "Services are billed monthly under the terms set out in your engagement agreement, with invoices payable on the agreed cadence.",
      "Either party may end an engagement with the notice period stated in that agreement. On termination we return your records and revoke our access to your systems.",
      "You remain responsible for the accuracy and completeness of the source documents and system access you provide to us.",
    ],
  },
  {
    h: "No professional advice through this site",
    p: [
      "Content published on this site, including guides and articles, is general information. It is not accounting, tax, audit, or legal advice, and it does not create a professional relationship.",
      "Do not act on site content without advice tailored to your circumstances.",
    ],
  },
  {
    h: "Intellectual property",
    p: [
      "The Finendra name, brand marks, site design, and published content are owned by Finendra. You may share and quote our published material with attribution, but not republish it as your own.",
      "Documents we prepare for you under an engagement are covered by that engagement agreement.",
    ],
  },
  {
    h: "Confidentiality",
    p: [
      "Information you share with us in the course of an enquiry or engagement is treated as confidential and shared internally only with the people who need it to do the work.",
      "Our personnel are bound by confidentiality obligations.",
    ],
  },
  {
    h: "Availability and changes",
    p: [
      "We aim to keep this site available and accurate, but we do not guarantee uninterrupted availability or that every detail is current at all times.",
      "We may update these terms; the current version is always the one published on this page. Questions can be sent to connect@finendra.com.",
    ],
  },
];

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service | Finendra" },
      {
        name: "description",
        content:
          "Terms governing use of the Finendra website, the status of quotes and estimates, engagement basics, confidentiality, and intellectual property.",
      },
      { property: "og:title", content: "Terms of Service | Finendra" },
      {
        property: "og:description",
        content: "Website terms, how quotes and estimates work, and how engagements are governed.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: OG },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Terms of Service"
        body="This page is maintained by Finendra and covers use of this website. Client engagements are governed by a separate signed service agreement."
      />

      <section className="px-6 pb-28">
        <div className="mx-auto max-w-3xl space-y-12">
          {sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.03}>
              <h2 className="font-display text-2xl font-bold text-brand-navy">{s.h}</h2>
              <div className="mt-4 space-y-4 leading-relaxed text-brand-navy/65">
                {s.p.map((para) => (
                  <p key={para}>{para}</p>
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
